import type { ColumnDef, ExpandedState, Row } from '@tanstack/react-table';
import type { CSSProperties, ReactNode } from 'react';

/**
 * カラム幅のプリセット定義(padding(8px * 2 = 16px)込み)
 */
export const WIDTH_PRESETS = {
  auto: undefined, // 幅指定なし（auto）
  xxs: 48, // 48px = 32px + 16px
  xs: 96, // 96px = 80px + 16px
  s: 136, // 136px = 120px + 16px
  m: 176, // 176px = 160px + 16px
  l: 216, // 216px = 200px + 16px
} as const;

export type WidthPreset = keyof typeof WIDTH_PRESETS;

/**
 * TanStack TableのColumnMetaを拡張
 */
declare module '@tanstack/react-table' {
  interface ColumnMeta<TData, TValue> {
    width?: number | WidthPreset | 'auto';
    minWidth?: number | WidthPreset | 'auto';
    /**
     * カラムグループ（最上段ヘッダー）で均等割りしたい場合のウェイト
     * 同じグループ階層の中で比率算出に利用
     */
    groupWidthWeight?: number;
    /**
     * カラム幅を比率指定したい場合のウェイト（分割カラム用）
     * 未指定時は1として扱う
     */
    columnWidthWeight?: number;
    /**
     * カラムを左端または右端に固定する
     * 水平スクロール時も固定位置に表示される
     * 固定カラムには明示的な幅（width）の指定が必要
     */
    pinned?: 'left' | 'right';
  }
}

/**
 * 展開可能な行の設定
 */
export type ExpandableConfig<TData> = {
  /**
   * 拡張行のレンダリング関数
   * @param row - 展開された行のデータ
   * @returns レンダリングするReactノード
   */
  expandedRowRender?: (row: Row<TData>) => ReactNode;
  /**
   * 行が展開可能かどうかを判定する関数
   * @param row - 判定対象の行
   * @returns 展開可能な場合true
   */
  rowExpandable?: (row: Row<TData>) => boolean;
  /**
   * サブ行を取得する関数（ネストテーブル用）
   * @param originalRow - 元の行データ
   * @param index - 行のインデックス
   * @returns サブ行の配列
   */
  getSubRows?: (originalRow: TData, index: number) => TData[] | undefined;
  /**
   * 行IDを取得する関数
   * @param originalRow - 元の行データ
   * @param index - 行のインデックス
   * @param parent - 親行（ネスト時）
   * @returns 行の一意なID
   */
  getRowId?: (originalRow: TData, index: number, parent?: Row<TData>) => string;
};

/**
 * Stickyヘッダーの設定
 */
export type StickyConfig = {
  /**
   * ヘッダーのtop位置オフセット（px）
   * 他のsticky要素（ナビゲーションバー等）がある場合に設定
   * @default PAGE_LAYOUT_FRAME_HEIGHT
   */
  offsetHeader?: number;
  /**
   * ボディの最大高さ（px）
   * 設定するとボディがスクロール可能になる
   */
  maxBodyHeight?: number;
};

/**
 * ピン留めカラムのスタイル情報
 */
export type PinnedColumnInfo = {
  /** インラインスタイル（position: relative + zIndex） */
  style: CSSProperties;
  /** ピン留め方向 */
  side: 'left' | 'right';
  /** ピン留めセルのtranslateX打ち消し用オフセット値（累積幅） */
  pinnedOffset: number;
  /** 左固定の最後のカラムかどうか（影表示用） */
  isLastLeftPinned: boolean;
  /** 右固定の最初のカラムかどうか（影表示用） */
  isFirstRightPinned: boolean;
};

/**
 * 仮想スクロールの設定
 */
export type VirtualScrollConfig = {
  /** スクロールコンテナの高さ（数値: px、文字列: CSS値） */
  height: number | string;
  /** 表示領域外に余分にレンダリングする行数。デフォルト: 10 */
  overscan?: number;
};

/**
 * DataTableコンポーネントのProps型定義
 */
export type DataTableProps<TData, TValue> = {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading?: boolean;
  pagination: {
    totalCount: number | undefined;
    /** ページ番号（1ベース、URLパラメータと同じ） */
    page: number | undefined;
    pageSize: number | undefined;
  } | null;
  rightActionChildren?: ReactNode;
  filterPopover?: ReactNode;
  /** ツールバー左側、表示設定の右隣に表示するスロット（一括操作メニュー等） */
  bulkActions?: ReactNode;
  /** ツールバー行の下にフィルタータグ等を表示するスロット */
  filterTags?: ReactNode;
  /** フィルタータグ行の下、テーブル本体の上に表示するスロット（行選択時のアクションバー等） */
  selectionBar?: ReactNode;
  /**
   * 表示設定Popoverの制御
   * - true: PageSizeSelector のみ表示
   * - ReactNode: PageSizeSelector に加えて追加設定（金額単位など）を children として差し込む
   * - false/undefined: 非表示
   */
  displaySettings?: boolean | { children: ReactNode; hasCustomSettings: boolean };
  expanded?: ExpandedState | undefined;
  onExpandedChange?: (expanded: ExpandedState) => void;
  expandable?: ExpandableConfig<TData>;
  showLastBorderBottom?: boolean;
  emptyRender?: ReactNode;
  /**
   * ハイライト表示する行のID配列
   * 指定された行には緑の枠が表示される
   * expandableのgetRowIdで指定したIDを使用する必要がある
   */
  highlightedRowIds?: string[];
  /**
   * 選択状態として表示する行のID配列
   * 指定された行には選択ハイライト背景色が適用される
   * expandableのgetRowIdで指定したIDを使用する必要がある
   */
  selectedRowIds?: string[];
  /**
   * スクロール対象の行ID
   * 指定された行が表示領域に入るようスクロールする
   */
  scrollToRowId?: string | null;
  /**
   * Stickyヘッダーの設定
   * trueを指定するとデフォルト設定でstickyヘッダーが有効になる
   * オブジェクトを指定すると詳細な設定が可能
   */
  sticky?: boolean | StickyConfig;
  /**
   * 仮想スクロール設定（React Virtuoso ベース）
   * 指定すると画面外の行をレンダリングしない（パフォーマンス改善用）
   * 通常モード（非Sticky）でのみ有効
   */
  virtualScroll?: VirtualScrollConfig;
};
